// src/hooks/useAuth.js
import { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged, signInWithPopup, signInWithEmailAndPassword,
  createUserWithEmailAndPassword, signOut, updateProfile,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { auth, db, googleProvider } from "../lib/firebase";
import { ONI_FORMS } from "../data/demons";

const AuthContext = createContext(null);

function enrichProfile(profileData) {
  if (!profileData?.demon?.oni?.id) return profileData;
  const oniId = profileData.demon.oni.id;
  const fresh  = ONI_FORMS.find(o => o.id === oniId);
  if (!fresh) return profileData;
  return {
    ...profileData,
    demon: {
      ...profileData.demon,
      oni: { ...fresh, ...profileData.demon.oni, image: fresh.image, emoji: fresh.emoji },
    },
  };
}

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(undefined);
  const [profile, setProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        const snap = await getDoc(doc(db, "users", u.uid));
        setProfile(snap.exists() ? enrichProfile(snap.data()) : null);
        try {
          const adminSnap = await getDoc(doc(db, "config", "admins"));
          if (adminSnap.exists()) {
            const adminList = adminSnap.data().emails || [];
            setIsAdmin(adminList.includes(u.email));
          }
        } catch(e) { setIsAdmin(false); }
      } else {
        setProfile(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const loginGoogle = async () => {
    const result = await signInWithPopup(auth, googleProvider);
    await ensureUserDoc(result.user);
  };

  const loginEmail = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  const registerEmail = async (email, password, displayName) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(result.user, { displayName });
    await ensureUserDoc(result.user, displayName);
  };

  const logout = () => signOut(auth);

  const ensureUserDoc = async (u, displayName) => {
    const ref  = doc(db, "users", u.uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, {
        uid: u.uid, email: u.email,
        displayName: displayName || u.displayName || "Oni inconnu",
        demon: null, createdAt: serverTimestamp(),
      });
    }
  };

  const refreshProfile = async () => {
    if (!user) return;
    const snap = await getDoc(doc(db, "users", user.uid));
    setProfile(snap.exists() ? enrichProfile(snap.data()) : null);
  };

  return (
    <AuthContext.Provider value={{ user, profile, isAdmin, loading, loginGoogle, loginEmail, registerEmail, logout, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
